import { PolicyAssignScope } from "./utils";
import {
  Box,
  CircularProgress,
  Grid,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { AddButton } from "~/components/add-button";
import { setDocumentTitle } from "~/lib/commonUtils";
import { usePolicyPermissions } from "~/pages/security/policies/hooks/usePolicyPermissions";
import useRegistries from "~/pages/security/policies/hooks/useRegistries";
import { useNavigate } from "react-router-dom";
import { PolicyMrnToURIEncodedId } from "~/lib/mrn";
import { RegistriesByCategory } from "~/pages/security/policies/components/RegistriesByCategory";
import { Facets } from "~/pages/security/policies/components/Facets";
import { Flex } from "~/components/Flex";
import { NoSearchResults } from "~/pages/Search";
import { Fragment, ReactNode } from "react";
import { Text } from "~/components/ui-library";
import { policyIcon } from "~/pages/inventory/utils/policyIcon";
import { Caption } from "~/pages/security/policies/components/Caption";
import { PolicyAssignButton } from "~/pages/security/policies/components/PolicyAssignButton";
import { Pagination } from "~/components/pagination";
import { buildScopedUrl } from "./utils";

export interface PoliciesAddPageProps {
  scope: PolicyAssignScope;
  basePath: string;
  sectionLabel: string;
  breadcrumbs: ReactNode;
}

export function PoliciesAddPage({
  scope,
  basePath,
  sectionLabel,
  breadcrumbs,
}: PoliciesAddPageProps) {
  const title = "Add Policies";
  setDocumentTitle([title, sectionLabel]);

  const {
    loading,
    error,
    filtersLoading,
    filtersErrors,
    filterOptions,
    sortByOptions,
    categoryOptions,
    paginatedRegistries,
    paginationProps,
    noPolicies,
    facets,
    state,
    filters,
    onFacetsChange,
    onChangeAssignHandler,
  } = useRegistries({ scope });
  const navigate = useNavigate();
  const { policyAssignPermission } = usePolicyPermissions();

  const handlePolicyAssignChange = async () => {
    try {
      await onChangeAssignHandler();
    } catch (error) {
      console.error("Failed to update policy assignment", error);
    }
  };

  const policyAddDetailHref = (mrn: string) => {
    return buildScopedUrl(
      `${basePath}/add/${PolicyMrnToURIEncodedId(mrn)}`,
      scope,
    );
  };

  return (
    <Box>
      {breadcrumbs}
      <Stack direction="row" justifyContent="space-between" mb={5}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
        <AddButton href={basePath} close />
      </Stack>

      {state.isReady ? (
        <>
          {categoryOptions.length > 0 && (
            <RegistriesByCategory
              categoryOptions={categoryOptions}
              categories={facets.categories}
              onFacetsChange={onFacetsChange}
            />
          )}
          <Box mt={5}>
            <Facets
              filtersLoading={filtersLoading}
              filtersErrors={filtersErrors}
              filterOptions={filterOptions}
              filters={filters}
              sortByOptions={sortByOptions}
              facets={facets}
              onFacetsChange={onFacetsChange}
            />
          </Box>
          {loading && (
            <Flex center>
              <CircularProgress />
            </Flex>
          )}
          {error && (
            <Box>
              <Text>Errors</Text>
              <Text>{String(error)}</Text>
            </Box>
          )}
          {!loading && !error && noPolicies && (
            <NoSearchResults searchQuery={facets.query} name="Policies" />
          )}
          {paginatedRegistries.length > 0 && (
            <Fragment>
              <List
                id="available-policies-list"
                component={Paper}
                sx={(theme) => ({
                  py: 0,
                  overflow: "hidden",
                  borderRadius: theme.spacing(0.5),
                  boxShadow: (theme) => theme.shadows[1],
                })}
              >
                {paginatedRegistries.map((pack) => {
                  return (
                    <ListItem
                      key={pack.mrn}
                      className="available-policies-list-item"
                      sx={{
                        p: 3,
                        cursor: "pointer",
                        "&:not(:last-child)": {
                          borderBottom: (theme) =>
                            `1px solid ${theme.palette.background.lighter}`,
                        },
                        "&:hover": {
                          backgroundColor: (theme) =>
                            theme.palette.background.light,
                        },
                      }}
                      onClick={() => navigate(policyAddDetailHref(pack.mrn))}
                    >
                      <Grid container spacing={0} size="grow">
                        <Grid size={10}>
                          <Stack direction="row" gap={3}>
                            {policyIcon(pack.name, "large")}
                            <Stack>
                              <Typography
                                sx={{ fontWeight: 700 }}
                                className="item-name"
                              >
                                {pack.name}
                              </Typography>
                              <Caption
                                trustLevel={pack.trustLevel}
                                version={pack.version}
                                authors={pack.authors}
                              />
                            </Stack>
                          </Stack>
                        </Grid>
                        <Grid
                          size={2}
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-end"
                        >
                          {policyAssignPermission && (
                            <PolicyAssignButton
                              scope={scope}
                              policyMrn={pack.mrn}
                              assigned={pack.assigned}
                              assignedScope={pack.assignedScope}
                              action={pack.action}
                              onChange={handlePolicyAssignChange}
                              advanced
                            />
                          )}
                        </Grid>
                      </Grid>
                    </ListItem>
                  );
                })}
              </List>
              <Pagination {...paginationProps} />
            </Fragment>
          )}
        </>
      ) : null}
    </Box>
  );
}
